/**
 * CalloutBlock — TipTap block node for Obsidian-style callouts.
 *
 *   > [!warning] Title goes here
 *   > Body paragraph
 *   > Continues here
 *
 * Stored as a block node containing standard block content (paragraphs,
 * lists, etc.) plus two attrs: `calloutKind` and `calloutTitle`.
 */

import { Node, mergeAttributes } from '@tiptap/core';

export interface CalloutAttrs {
  calloutKind: string;
  calloutTitle: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      setCallout: (attrs: CalloutAttrs) => ReturnType;
      unsetCallout: () => ReturnType;
    };
  }
}

export const CalloutBlock = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      calloutKind: {
        default: 'note',
        parseHTML: (el) =>
          el.getAttribute('data-callout-kind') ?? 'note',
        renderHTML: (attrs) => ({
          'data-callout-kind': attrs.calloutKind ?? 'note',
        }),
      },
      calloutTitle: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-callout-title') ?? '',
        renderHTML: (attrs) => ({
          'data-callout-title': attrs.calloutTitle ?? '',
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div.vault-callout' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes({ class: 'vault-callout' }, HTMLAttributes),
      [
        'div',
        { class: 'vault-callout-title' },
        `[${HTMLAttributes['data-callout-kind']}] ${HTMLAttributes['data-callout-title'] ?? ''}`,
      ],
      ['div', { class: 'vault-callout-body' }, 0],
    ];
  },

  addCommands() {
    return {
      setCallout:
        (attrs) =>
        ({ commands }) =>
          commands.wrapIn(this.name, attrs),
      unsetCallout:
        () =>
        ({ commands }) =>
          commands.lift(this.name),
    };
  },
});

const CALLOUT_START = /^>\s*\[!([A-Za-z][A-Za-z0-9-]*)\](.*)$/;

export interface CalloutBlockToken {
  kind: 'plain' | 'callout';
  /** For plain blocks: original lines verbatim. */
  lines: string[];
  /** For callout blocks: kind, title, inner markdown lines. */
  calloutKind?: string;
  calloutTitle?: string;
}

/**
 * Pre-pass for the markdown-to-doc converter. Splits a body into
 * "plain markdown chunks" and "callout chunks" so downstream paragraph
 * parsing doesn't have to know about callouts.
 */
export function extractCallouts(body: string): CalloutBlockToken[] {
  const lines = body.split(/\r?\n/);
  const out: CalloutBlockToken[] = [];
  let i = 0;
  let plain: string[] = [];
  function flushPlain() {
    if (plain.length > 0) {
      out.push({ kind: 'plain', lines: plain });
      plain = [];
    }
  }
  while (i < lines.length) {
    const line = lines[i]!;
    const m = line.match(CALLOUT_START);
    if (m) {
      flushPlain();
      const calloutKind = m[1]!.toLowerCase();
      const calloutTitle = m[2]!.trim();
      const innerLines: string[] = [];
      i++;
      while (i < lines.length && lines[i]!.startsWith('>')) {
        innerLines.push(lines[i]!.replace(/^>\s?/, ''));
        i++;
      }
      out.push({ kind: 'callout', lines: innerLines, calloutKind, calloutTitle });
      continue;
    }
    plain.push(line);
    i++;
  }
  flushPlain();
  return out;
}
